import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalHotkeys } from '../../contexts/HotkeysContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Sun, 
  Moon, 
  Bell,
  LogOut,
  User,
  Building2,
  ChevronDown,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
  ChevronRight,
  Plus,
  Check,
  Search,
  Command,
  Monitor,
  Palette
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials, cn } from '../../lib/utils';
import { dashboardAPI, companyAPI } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Motivational phrases for the header
const MOTIVATIONAL_PHRASES = [
  { headline: "Decisões inteligentes começam com dados confiáveis.", subtext: "A OFS organiza o que importa." },
  { headline: "Menos planilhas, mais controle.", subtext: "Sua gestão financeira em um só lugar." },
  { headline: "Ação que gera resultado.", subtext: "É isso que a OFS entrega todos os dias." },
  { headline: "Fluxo de caixa no controle.", subtext: "Resultados que aparecem." },
  { headline: "Organize suas finanças hoje.", subtext: "Construa seu futuro com clareza." },
];

const ALERT_ICONS = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};

const ALERT_STYLES = {
  error: {
    bg: 'bg-red-50 dark:bg-red-950/50',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-800 dark:text-red-200',
    text: 'text-red-700 dark:text-red-300',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/50',
    border: 'border-amber-200 dark:border-amber-800',
    icon: 'text-amber-600 dark:text-amber-400',
    title: 'text-amber-800 dark:text-amber-200',
    text: 'text-amber-700 dark:text-amber-300',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/50',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-800 dark:text-blue-200',
    text: 'text-blue-700 dark:text-blue-300',
  },
  success: {
    bg: 'bg-green-50 dark:bg-green-950/50',
    border: 'border-green-200 dark:border-green-800',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-800 dark:text-green-200',
    text: 'text-green-700 dark:text-green-300',
  },
};

export function Header() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { user, logout, updateUser } = useAuth();
  const { openSearch } = useGlobalHotkeys();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [alertsCount, setAlertsCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [currentCompanyId, setCurrentCompanyId] = useState(user?.current_company_id);
  
  // Random phrase on mount
  const [phraseIndex] = useState(() => 
    Math.floor(Math.random() * MOTIVATIONAL_PHRASES.length)
  );
  const phrase = MOTIVATIONAL_PHRASES[phraseIndex];

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await dashboardAPI.getAlerts();
      setAlerts(response.data.alerts || []);
      setAlertsCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await companyAPI.list();
      setCompanies(response.data.companies || []);
      setCurrentCompanyId(response.data.current_company_id);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchCompanies();
    // Refresh alerts every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts, fetchCompanies]);

  const handleAlertClick = (alert) => {
    if (alert.action) {
      navigate(alert.action);
    }
  };

  const handleSwitchCompany = async (companyId) => {
    try {
      const response = await companyAPI.switch(companyId);
      setCurrentCompanyId(companyId);
      
      // Update token in localStorage
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }
      
      // Update user context with new company
      if (response.data.company) {
        updateUser({ 
          current_company_id: companyId,
          company_name: response.data.company.name 
        });
      }
      
      toast.success(`Empresa alterada para ${response.data.company.name}`);
      
      // Reload page to refresh all data with new company context
      window.location.reload();
    } catch (error) {
      toast.error('Erro ao trocar de empresa');
    }
  };

  const currentCompany = companies.find(c => c.id === currentCompanyId);

  return (
    <header 
      data-testid="header"
      className="sticky top-0 z-30 flex h-14 items-center justify-between px-6"
      style={{ 
        background: 'linear-gradient(90deg, #0B1F33 0%, #1C3552 50%, #2E4A6F 100%)',
      }}
    >
      {/* Left side - Institutional text */}
      <div className="flex flex-col justify-center">
        <p className="text-sm font-medium leading-tight text-white">
          {phrase.headline}
        </p>
        <p className="text-xs text-white/70">
          {phrase.subtext}
        </p>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-1">
        {/* Search Button */}
        <Button 
          variant="ghost" 
          className="hidden sm:flex items-center gap-2 px-3 h-9 hover:bg-white/10 text-white/90"
          onClick={openSearch}
          data-testid="global-search-btn"
          data-tour="search"
        >
          <Search className="h-4 w-4" />
          <span className="text-xs">Buscar</span>
          <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-medium">
            <Command className="h-3 w-3" />K
          </kbd>
        </Button>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative h-9 w-9 hover:bg-white/10 focus:bg-white/20 active:bg-white/20"
              data-testid="notifications-btn"
              data-tour="notifications"
            >
              <Bell className="h-4 w-4 text-white/90" />
              {alertsCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse">
                  {alertsCount > 9 ? '9+' : alertsCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="w-80 p-0"
            sideOffset={8}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h4 className="font-semibold text-sm">Notificações</h4>
              {alertsCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {alertsCount} {alertsCount === 1 ? 'alerta' : 'alertas'}
                </Badge>
              )}
            </div>
            
            <ScrollArea className="max-h-80">
              {alerts.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Tudo certo por aqui!</p>
                  <p className="text-xs">Nenhuma notificação pendente.</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {alerts.map((alert, index) => {
                    const Icon = ALERT_ICONS[alert.type] || Info;
                    const styles = ALERT_STYLES[alert.type] || ALERT_STYLES.info;
                    
                    return (
                      <div
                        key={index}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md",
                          styles.bg,
                          styles.border
                        )}
                        onClick={() => handleAlertClick(alert)}
                      >
                        <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", styles.icon)} />
                        <div className="flex-1 min-w-0">
                          <p className={cn("font-medium text-sm leading-tight", styles.title)}>
                            {alert.title}
                          </p>
                          <p className={cn("text-xs mt-0.5", styles.text)}>
                            {alert.message}
                          </p>
                        </div>
                        <ChevronRight className={cn("h-4 w-4 flex-shrink-0", styles.icon)} />
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
            
            {alerts.length > 0 && (
              <div className="p-2 border-t">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-xs"
                  onClick={() => navigate('/dashboard')}
                >
                  Ver todos no Dashboard
                </Button>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-9 w-9 hover:bg-white/10"
              data-testid="theme-toggle"
            >
              {resolvedTheme === 'light' ? (
                <Sun className="h-4 w-4 text-amber-400" />
              ) : (
                <Moon className="h-4 w-4 text-blue-300" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuLabel className="text-xs">Tema</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => setTheme('light')}
              className={cn(theme === 'light' && 'bg-accent')}
            >
              <Sun className="mr-2 h-4 w-4" />
              Claro
              {theme === 'light' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setTheme('dark')}
              className={cn(theme === 'dark' && 'bg-accent')}
            >
              <Moon className="mr-2 h-4 w-4" />
              Escuro
              {theme === 'dark' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setTheme('system')}
              className={cn(theme === 'system' && 'bg-accent')}
            >
              <Monitor className="mr-2 h-4 w-4" />
              Sistema
              {theme === 'system' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Company Selector */}
        {companies.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="hidden md:flex items-center gap-2 px-3 h-9 hover:bg-white/10 max-w-[200px]"
                data-testid="company-selector-btn"
                data-tour="company-selector"
              >
                <Building2 className="h-4 w-4 text-white/90 flex-shrink-0" />
                <span className="text-xs font-medium text-white truncate">
                  {currentCompany?.name || user?.company_name || 'Empresa'}
                </span>
                <ChevronDown className="h-3 w-3 text-white/70 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Trocar Empresa
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <ScrollArea className="max-h-60">
                {companies.map((company) => (
                  <DropdownMenuItem
                    key={company.id}
                    onClick={() => handleSwitchCompany(company.id)}
                    className={cn(
                      "flex items-center justify-between cursor-pointer",
                      currentCompanyId === company.id && "bg-accent"
                    )}
                    data-testid={`company-option-${company.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">{company.name}</span>
                      {company.trade_name && (
                        <span className="text-xs text-muted-foreground">{company.trade_name}</span>
                      )}
                    </div>
                    {currentCompanyId === company.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </ScrollArea>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/companies')}
                className="text-primary"
                data-testid="manage-companies-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Gerenciar Empresas
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="flex items-center gap-2 pl-2 pr-1 h-9 hover:bg-white/10"
              data-testid="user-menu-btn"
              data-tour="user-menu"
            >
              <Avatar className="h-7 w-7 ring-1 ring-white/30">
                {user?.avatar_url ? (
                  <AvatarImage 
                    src={user.avatar_url} 
                    alt={user?.name || 'Usuário'}
                    className="object-cover"
                    style={{ imageRendering: 'auto' }}
                  />
                ) : null}
                <AvatarFallback className="bg-[#2DD4FF] text-[#0B1F33] text-xs font-semibold">
                  {getInitials(user?.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start text-left">
                <span className="text-xs font-medium text-white">{user?.name || 'Usuário'}</span>
                <span className="text-[10px] text-white/70">{user?.company_name || 'Empresa'}</span>
              </div>
              <ChevronDown className="h-3 w-3 text-white/70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              data-testid="profile-menu-item"
              onClick={() => navigate('/settings')}
            >
              <User className="mr-2 h-4 w-4" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="company-menu-item">
              <Building2 className="mr-2 h-4 w-4" />
              Empresa
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={logout}
              className="text-destructive focus:text-destructive"
              data-testid="logout-menu-item"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
